import React, { useRef } from 'react';

const fmt = (n) => Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PAY_LABELS = {
    cash: 'Cash',
    transfer: 'Bank Transfer',
    pos: 'POS / Card',
    split: 'Split Payment',
};

export default function ReceiptModal({ data, onClose }) {
    const {
        transaction,
        cart,
        customer,
        subtotal,
        discountValue,
        redemptionValue,
        taxAmount,
        taxRate,
        totalAfterDiscounts,
        changeAmount,
        pointsRedeemed,
        payMethod,
        payReference,
        cashierName,
        storeName,
    } = data;

    const printRef = useRef(null);

    const handlePrint = () => {
        const win = window.open('', '_blank', 'width=380,height=700');
        if (!win) {
            window.alert('Please allow popups so the receipt can be printed.');
            return;
        }
        win.document.write(`
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Receipt ${transaction?.transaction_id || ''}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; color: #000; margin: 0; padding: 12px; max-width: 320px; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 4px; }
  .center { text-align: center; }
  .divider { border: none; border-top: 1px dashed #000; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  td { padding: 2px 0; }
  td:last-child { text-align: right; }
  .total-row td { font-weight: bold; border-top: 1px dashed #000; padding-top: 4px; }
  .summary td:last-child { text-align: right; }
  .footer { text-align: center; margin-top: 12px; font-size: 11px; }
  @media print { @page { size: 80mm auto; margin: 0; } body { padding: 4mm; } }
</style>
</head>
<body>
${printRef.current?.innerHTML || ''}
</body>
</html>`);
        win.document.close();
        setTimeout(() => {
            win.focus();
            win.print();
            setTimeout(() => win.close(), 250);
        }, 150);
    };

    const now = transaction?.created_at ? new Date(transaction.created_at) : new Date();
    const dateStr = now.toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });

    return (
        <div className="receipt-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="receipt-modal">
                <div className="receipt-modal-header">
                    <h3>Sale Complete</h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-sm btn-outline" onClick={handlePrint}>🖨 Print</button>
                        <button className="btn btn-sm btn-dark" onClick={onClose}>Done</button>
                    </div>
                </div>

                {/* Printable area */}
                <div className="receipt-paper" ref={printRef}>
                    <h1>{storeName}</h1>
                    <p className="center" style={{ margin: '2px 0', fontSize: 11 }}>Point of Sale Receipt</p>
                    <hr className="divider" />
                    <p style={{ margin: '2px 0' }}><b>Receipt:</b> {transaction?.transaction_id}</p>
                    <p style={{ margin: '2px 0' }}><b>Date:</b> {dateStr} {timeStr}</p>
                    <p style={{ margin: '2px 0' }}><b>Cashier:</b> {cashierName}</p>
                    {customer && <p style={{ margin: '2px 0' }}><b>Customer:</b> {`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Walk-in customer'} — {customer.phone}</p>}
                    <hr className="divider" />

                    <table>
                        <thead>
                            <tr>
                                <td><b>Item</b></td>
                                <td style={{ textAlign: 'center' }}><b>Qty</b></td>
                                <td style={{ textAlign: 'right' }}><b>Price</b></td>
                                <td style={{ textAlign: 'right' }}><b>Total</b></td>
                            </tr>
                        </thead>
                        <tbody>
                            {cart.map((item) => (
                                <tr key={item.product_id}>
                                    <td>{item.name}</td>
                                    <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                                    <td style={{ textAlign: 'right' }}>₦{fmt(item.price)}</td>
                                    <td style={{ textAlign: 'right' }}>₦{fmt(item.price * item.quantity)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <hr className="divider" />

                    <table className="summary">
                        <tbody>
                            <tr><td>Subtotal</td><td>₦{fmt(subtotal)}</td></tr>
                            {discountValue > 0 && <tr><td>Discount</td><td>−₦{fmt(discountValue)}</td></tr>}
                            {redemptionValue > 0 && <tr><td>Loyalty ({pointsRedeemed} pts)</td><td>−₦{fmt(redemptionValue)}</td></tr>}
                            {taxAmount > 0 && <tr><td>Tax ({Number(taxRate || 0).toFixed(2)}%)</td><td>₦{fmt(taxAmount)}</td></tr>}
                            <tr className="total-row"><td><b>Total</b></td><td><b>₦{fmt(totalAfterDiscounts)}</b></td></tr>
                        </tbody>
                    </table>

                    <hr className="divider" />

                    <table>
                        <tbody>
                            <tr><td>Payment</td><td>{PAY_LABELS[payMethod] || payMethod}</td></tr>
                            {payReference && <tr><td>Reference</td><td>{payReference}</td></tr>}
                            {payMethod === 'cash' && <tr><td>Cash tendered</td><td>₦{fmt(data.cashTendered)}</td></tr>}
                            {changeAmount > 0 && <tr><td><b>Change</b></td><td><b>₦{fmt(changeAmount)}</b></td></tr>}
                        </tbody>
                    </table>

                    {customer && (
                        <>
                            <hr className="divider" />
                            <p style={{ margin: '2px 0', fontSize: 11 }}>
                                <b>Loyalty:</b> {pointsRedeemed > 0 ? `${pointsRedeemed} pts redeemed. ` : ''}
                                Points earned this sale: {Math.floor(Number(totalAfterDiscounts) * 0.01)}.
                            </p>
                        </>
                    )}

                    <div className="footer">
                        <hr className="divider" />
                        <p>Thank you for your purchase!</p>
                        <p style={{ fontSize: 10, marginTop: 4 }}>Powered by Royaltech POS</p>
                    </div>
                </div>

                {/* Change highlight for cashier */}
                {changeAmount > 0 && (
                    <div className="receipt-change-banner">
                        <span>Change to give</span>
                        <strong>₦{fmt(changeAmount)}</strong>
                    </div>
                )}
            </div>
        </div>
    );
}
